// src/pages/public/HomePage.jsx
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { productsApi, promotionsApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Heart, ShoppingCart, ChevronRight, Star, Zap } from 'lucide-react'
import { useCart } from '@/context/CartContext'
import toast from 'react-hot-toast'

const CATEGORIES = [
  { name: 'Cotton',    emoji: '🌿', slug: 'cotton',    desc: 'Breathable everyday fabrics' },
  { name: 'Silk',      emoji: '✨', slug: 'silk',       desc: 'Luxurious & lustrous weaves' },
  { name: 'Linen',     emoji: '🏔️', slug: 'linen',     desc: 'Cool, crisp summer fabrics' },
  { name: 'Synthetic', emoji: '⚗️', slug: 'synthetic', desc: 'Durable technical fabrics' },
  { name: 'Wool',      emoji: '🐑', slug: 'wool',       desc: 'Warm winter staples' },
  { name: 'Blends',    emoji: '🎨', slug: 'blends',     desc: 'Best of both worlds' },
]

function ProductCard({ product }) {
  const { format } = useCurrency()
  const { addItem, loading } = useCart()

  const handleAdd = async () => {
    try {
      await addItem(product.id, product.moq || 1)
      toast.success('Added to cart!')
    } catch { toast.error('Could not add to cart') }
  }

  return (
    <div className="group bg-white border border-border rounded-2xl overflow-hidden hover:shadow-card-md transition-shadow">
      <Link to={`/fabrics/${product.slug}`} className="block aspect-[4/3] bg-surface overflow-hidden">
        {product.primary_image ? (
          <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">🧵</div>
        )}
      </Link>
      <div className="p-4">
        <p className="text-xs text-muted font-medium mb-0.5">{product.category?.name}</p>
        <Link to={`/fabrics/${product.slug}`} className="font-bold text-sm text-gray-900 hover:text-pink transition-colors line-clamp-1">
          {product.name}
        </Link>
        <div className="flex items-center gap-1 mt-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={11} className={i < Math.floor(product.average_rating || 0) ? 'text-orange fill-orange' : 'text-gray-200 fill-gray-200'} />
          ))}
          {product.reviews_count > 0 && <span className="text-xs text-muted">({product.reviews_count})</span>}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-extrabold text-base text-gray-900">{format(product.price)}</span>
            <span className="text-xs text-muted ml-1">/{product.unit || 'm'}</span>
          </div>
          <button onClick={handleAdd} disabled={loading}
            className="w-9 h-9 rounded-full bg-gradient-brand text-white flex items-center justify-center hover:shadow-brand-sm transition-all">
            <ShoppingCart size={15} />
          </button>
        </div>
        {product.moq && product.moq > 1 && (
          <p className="text-xs text-muted mt-1">Min {product.moq}{product.unit || 'm'}</p>
        )}
        {product.is_gi_tagged && (
          <span className="inline-block mt-1.5 text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
            🏷️ GI Tagged{product.gi_tag_name ? ` — ${product.gi_tag_name}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}

export default function HomePage() {
  const { data: featuredData } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () => productsApi.list({ featured: 1, per_page: 8 }).then(r => r.data),
  })

  const { data: newArrivals } = useQuery({
    queryKey: ['products', 'new'],
    queryFn: () => productsApi.list({ sort: 'newest', per_page: 4 }).then(r => r.data),
  })

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-white to-orange-50 py-16 md:py-24">
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-center">
            {/* Left — Text */}
            <div className="text-center md:text-left">
              <span className="inline-block bg-pink-light text-pink text-xs font-bold px-4 py-1.5 rounded-full mb-6">
                🇮🇳 India's #1 Textile Marketplace
              </span>
              <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight mb-6">
                Discover Premium<br />
                <span className="text-gradient">Fabrics & Textiles</span>
              </h1>
              <p className="text-lg md:text-xl text-muted max-w-xl mb-10">
                Connect with 2,000+ verified suppliers. Source cotton, silk, linen and more — with transparent pricing and direct bulk ordering.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Link to="/fabrics" className="btn-primary px-8 py-4 text-base">
                  Browse Fabrics <ChevronRight size={18} />
                </Link>
                <Link to="/register?role=supplier" className="btn-secondary px-8 py-4 text-base">
                  Become a Supplier
                </Link>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 mt-10 text-sm text-muted">
                {['2,000+ Suppliers', '50,000+ Products', '₹0 Listing Fee', '14-Day Free Trial'].map(t => (
                  <div key={t} className="flex items-center gap-1.5"><span className="text-pink">✓</span> {t}</div>
                ))}
              </div>
            </div>

            {/* Right — Image */}
            <div className="relative flex justify-center">
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-gradient-to-br from-pink/20 to-orange/20 rounded-3xl blur-2xl" />
                <img
                  src="/images/hero-bg.webp"
                  alt="Textile marketplace — vibrant fabrics and happy suppliers"
                  className="relative w-full rounded-2xl shadow-xl object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Categories ───────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display font-bold text-2xl md:text-3xl">Shop by Category</h2>
          <Link to="/fabrics" className="text-pink text-sm font-bold hover:underline flex items-center gap-1">
            View all <ChevronRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map(cat => (
            <Link key={cat.slug} to={`/fabrics?category=${cat.slug}`}
              className="group flex flex-col items-center p-5 rounded-2xl border border-border bg-white hover:border-pink hover:shadow-card-md transition-all text-center">
              <span className="text-4xl mb-3 group-hover:scale-110 transition-transform">{cat.emoji}</span>
              <span className="font-bold text-sm text-gray-900">{cat.name}</span>
              <span className="text-xs text-muted mt-0.5">{cat.desc}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Featured Products ─────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display font-bold text-2xl md:text-3xl">Featured Fabrics</h2>
          <Link to="/fabrics" className="text-pink text-sm font-bold hover:underline flex items-center gap-1">
            View all <ChevronRight size={15} />
          </Link>
        </div>
        {featuredData?.data?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {featuredData.data.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-surface border border-border animate-pulse">
                <div className="aspect-[4/3] bg-gray-100 rounded-t-2xl" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                  <div className="h-4 bg-gray-100 rounded w-full" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Flash Sales ──────────────────────────────────────────────── */}
      <FlashSalesSection />

      {/* ── Supplier CTA ─────────────────────────────────────────────── */}
      <section className="bg-gradient-brand text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl mb-4">Are you a textile supplier?</h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            List your fabrics for free and reach thousands of active buyers. Start with our 14-day free trial — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?role=supplier"
              className="inline-flex items-center gap-2 bg-white text-pink font-bold px-8 py-4 rounded-full text-base hover:shadow-lg transition-shadow">
              Start Selling Free <ChevronRight size={18} />
            </Link>
            <Link to="/pages/supplier-guide"
              className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-4 rounded-full text-base hover:bg-white/10 transition-colors">
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

// ── Countdown Timer ───────────────────────────────────────────────────────────
function CountdownTimer({ endsAt }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(endsAt) - Date.now()) / 1000)))

  useEffect(() => {
    if (remaining <= 0) return
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000)
    return () => clearInterval(t)
  }, [remaining > 0])

  const h = Math.floor(remaining / 3600)
  const m = Math.floor((remaining % 3600) / 60)
  const s = remaining % 60
  const pad = n => String(n).padStart(2, '0')

  if (remaining <= 0) return <span className="text-xs text-red-500 font-bold">Ended</span>

  return (
    <div className="flex gap-1">
      {[pad(h), pad(m), pad(s)].map((v, i) => (
        <span key={i} className="bg-gray-900 text-white text-xs font-mono font-bold px-1.5 py-1 rounded">{v}</span>
      ))}
    </div>
  )
}

// ── Flash Sales Section ───────────────────────────────────────────────────────
function FlashSalesSection() {
  const { format } = useCurrency()
  const { addItem } = useCart()
  const { data } = useQuery({
    queryKey: ['flash-sales'],
    queryFn: () => promotionsApi.flashSales().catch(() => null),
    refetchInterval: 60000,
  })
  const sales = data?.data?.data || data?.data || []
  if (!sales.length) return null

  return (
    <section className="max-w-7xl mx-auto px-4 pb-16">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <span className="bg-red-500 text-white p-2 rounded-xl animate-pulse"><Zap size={20} /></span>
          <div>
            <h2 className="font-display font-bold text-2xl md:text-3xl">Flash Sales</h2>
            <p className="text-xs text-muted">Limited time deals — grab them before they're gone!</p>
          </div>
        </div>
        <Link to="/fabrics?flash_sale=1" className="text-sm text-pink font-bold hover:underline flex items-center gap-1">
          View All <ChevronRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {sales.slice(0, 4).map(sale => (
          <div key={sale.id} className="group bg-white border-2 border-red-100 rounded-2xl overflow-hidden hover:shadow-card-md transition-all relative">
            <div className="absolute top-2 right-2 z-10 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {sale.discount_pct}% OFF
            </div>
            <Link to={`/fabrics/${sale.product?.slug || sale.product_id}`} className="block aspect-[4/3] bg-surface overflow-hidden">
              {sale.product?.primary_image
                ? <img src={sale.product.primary_image} alt={sale.product?.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                : <div className="w-full h-full flex items-center justify-center text-5xl">🧵</div>}
            </Link>
            <div className="p-4">
              <Link to={`/fabrics/${sale.product?.slug || sale.product_id}`} className="font-bold text-sm hover:text-pink transition-colors line-clamp-1">
                {sale.product?.name || 'Flash Sale Item'}
              </Link>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-extrabold text-base text-red-600">{format(sale.sale_price_inr)}</span>
                <span className="text-xs text-muted line-through">{format(sale.original_price_inr)}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <CountdownTimer endsAt={sale.ends_at} />
                <button onClick={() => addItem(sale.product_id, 1).then(() => toast.success('Added!')).catch(() => toast.error('Login to add'))}
                  className="w-8 h-8 rounded-full bg-gradient-brand text-white flex items-center justify-center hover:shadow-brand-sm transition-all">
                  <ShoppingCart size={13} />
                </button>
              </div>
              {sale.stock_limit && (
                <div className="mt-2">
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.max(5, 100 - (sale.units_sold / sale.stock_limit) * 100)}%` }} />
                  </div>
                  <p className="text-[10px] text-muted mt-0.5">{sale.stock_limit - (sale.units_sold || 0)} left</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
